import * as c from "@yahshi/contracts";
import { M1ApiError } from "../../m1-app/data/M1AppApi";
import type { LoginPolicyApi } from "./LoginPolicyApi";

type App = { id: string; name: string; status: "active" | "disabled" };
const fail = (code: string, message: string): never => { throw new M1ApiError(code, message); };
const clone = <T>(value: T): T => structuredClone(value);
const now = () => new Date().toISOString();
const defaults = () => clone(c.defaultLoginPolicyValues);

/** Ephemeral local adapter used only when VITE_ADMIN_RUNTIME_MODE=development. */
export function createDevelopmentLoginPolicyApi(options: { getApp: (id: string) => Promise<App>; listApps?: () => Promise<App[]> }): LoginPolicyApi {
  const policies = new Map<string, c.LoginPolicy>();
  const revisions = new Map<string, c.LoginPolicyRevision[]>();
  const audits = new Map<string, c.LoginPolicyAuditPage["items"]>();
  // Match the SQL adapter's actor-scoped receipts. This local adapter has one
  // actor; successful mutations retain their original response across edits.
  const mutations = new Map<string, { request: string; response: c.LoginPolicy }>();
  let queue: Promise<unknown> = Promise.resolve();
  const transaction = <T>(work: () => T | Promise<T>): Promise<T> => {
    const result = queue.then(work);
    queue = result.catch(() => undefined);
    return result.then(clone);
  };
  const app = async (id: string): Promise<App> => {
    const parsed = c.loginPolicyAppIdSchema.safeParse(id);
    const appId = parsed.success ? parsed.data : fail("LOGIN_POLICY_APP_NOT_FOUND", "关联应用不存在，请重新选择");
    try { return await options.getApp(appId); } catch { return fail("LOGIN_POLICY_APP_NOT_FOUND", "关联应用不存在，请重新选择"); }
  };
  const ensure = async (id: string) => {
    const selected = await app(id);
    let policy = policies.get(selected.id);
    if (!policy) {
      const timestamp = now();
      policy = { appId: selected.id, appName: selected.name, appStatus: selected.status, version: 1, draft: defaults(), published: null, createdAt: timestamp, updatedAt: timestamp, updatedBy: "dev-admin" };
      policies.set(selected.id, policy); revisions.set(selected.id, []); audits.set(selected.id, []);
    } else {
      policy.appName = selected.name;
      policy.appStatus = selected.status;
    }
    return policy;
  };
  const checkVersion = (actual: number, expected: number) => { if (actual !== expected) fail("LOGIN_POLICY_VERSION_CONFLICT", "策略已被其他管理员更新，请重新加载后重试"); };
  const bump = (value: number) => value < 2_147_483_647 ? value + 1 : fail("LOGIN_POLICY_INVALID_INPUT", "版本号已达到上限");
  const receipt = (action: "publish" | "rollback", appId: string, input: c.LoginPolicyPublishInput | c.LoginPolicyRollbackInput) => {
    const key = input.mutationId.toLowerCase();
    const request = JSON.stringify({ action, appId, input });
    const prior = mutations.get(key);
    if (prior && prior.request !== request) fail("LOGIN_POLICY_IDEMPOTENCY_CONFLICT", "操作标识已用于其他请求，请重新加载后重试");
    return { prior: prior?.response, commit: (policy: c.LoginPolicy) => mutations.set(key, { request, response: clone(policy) }) };
  };
  const record = (policy: c.LoginPolicy, action: "update" | "publish" | "rollback", revision: number | null = null) => {
    const rows = audits.get(policy.appId) ?? [];
    rows.unshift({ id: crypto.randomUUID(), actorId: "dev-admin", action, appId: policy.appId, version: policy.version, revision, requestId: null, createdAt: now() });
    audits.set(policy.appId, rows);
  };
  return {
    list: (input = {}) => transaction(async () => {
      const q = c.loginPolicyListQuerySchema.parse(input);
      const ids = options.listApps ? (await options.listApps()).map(app => app.id) : [...policies.keys()];
      const items = (await Promise.all(ids.map(id => ensure(id)))).filter(policy => (q.appStatus === "all" || policy.appStatus === q.appStatus) && `${policy.appName} ${policy.appId}`.toLocaleLowerCase().includes(q.keyword.toLocaleLowerCase()))
        .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt) || left.appId.localeCompare(right.appId));
      return { items: items.slice((q.page - 1) * q.pageSize, q.page * q.pageSize), total: items.length };
    }),
    get: id => transaction(() => ensure(id)),
    update: (id, input) => transaction(async () => {
      const value = c.loginPolicyUpdateSchema.parse(input); const policy = await ensure(id); checkVersion(policy.version, value.expectedVersion);
      if (JSON.stringify(policy.draft) !== JSON.stringify(value.values)) { const version = bump(policy.version); policy.draft = clone(value.values); policy.version = version; policy.updatedAt = now(); policy.updatedBy = "dev-admin"; record(policy, "update"); }
      return policy;
    }),
    publish: (id, input) => transaction(async () => {
      const value = c.loginPolicyPublishSchema.parse(input); const policy = await ensure(id);
      const mutation = receipt("publish", policy.appId, value);
      if (mutation.prior) return mutation.prior;
      checkVersion(policy.version, value.expectedVersion);
      if (policy.published && JSON.stringify(policy.published.values) === JSON.stringify(policy.draft)) fail("LOGIN_POLICY_UNCHANGED", "草稿与最新发布内容相同，无需重复发布");
      const revision = bump(policy.published?.revision ?? 0); const version = bump(policy.version); const published: c.LoginPolicyRevision = { revision, values: clone(policy.draft), publishedAt: now(), publishedBy: "dev-admin", action: "publish", sourceRevision: null };
      policy.published = published; policy.version = version; policy.updatedAt = now(); policy.updatedBy = "dev-admin"; revisions.get(policy.appId)!.unshift(published); record(policy, "publish", revision); mutation.commit(policy); return policy;
    }),
    rollback: (id, input) => transaction(async () => {
      const value = c.loginPolicyRollbackSchema.parse(input); const policy = await ensure(id);
      const mutation = receipt("rollback", policy.appId, value);
      if (mutation.prior) return mutation.prior;
      checkVersion(policy.version, value.expectedVersion);
      const source = revisions.get(policy.appId)?.find(revision => revision.revision === value.targetRevision); if (!source) return fail("LOGIN_POLICY_NOT_FOUND", "发布版本不存在");
      const revision = bump(policy.published?.revision ?? 0); const version = bump(policy.version); const published: c.LoginPolicyRevision = { revision, values: clone(source.values), publishedAt: now(), publishedBy: "dev-admin", action: "rollback", sourceRevision: source.revision };
      policy.draft = clone(source.values); policy.published = published; policy.version = version; policy.updatedAt = now(); policy.updatedBy = "dev-admin"; revisions.get(policy.appId)!.unshift(published); record(policy, "rollback", revision); mutation.commit(policy); return policy;
    }),
    revisions: (id, input = {}) => transaction(async () => { const policy = await ensure(id); const q = c.loginPolicyPaginationSchema.parse(input); const all = revisions.get(policy.appId) ?? []; return { items: all.slice((q.page - 1) * q.pageSize, q.page * q.pageSize), total: all.length }; }),
    audit: (id, input = {}) => transaction(async () => { const policy = await ensure(id); const q = c.loginPolicyPaginationSchema.parse(input); const all = audits.get(policy.appId) ?? []; return { items: all.slice((q.page - 1) * q.pageSize, q.page * q.pageSize), total: all.length }; }),
  };
}
